import { useCallback, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { acquireToken } from "../../authentication/utils/authUtils";
import { isAllowedPhoneNumber, normalizePhoneNumber } from "../phoneNumberUtils";
import {
  getApiErrorMessage,
  getReminderStatus,
  sendReminder,
} from "../api/reminderApi";

export const usePhoneReminder = ({ phoneNumber, parent, student }) => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() || accounts[0] || null;
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusKind, setStatusKind] = useState("info");

  const normalizedNumber = useMemo(
    () => normalizePhoneNumber(phoneNumber),
    [phoneNumber]
  );
  const validNumber = isAllowedPhoneNumber(normalizedNumber);
  const disabled = working || !validNumber;

  const send = useCallback(async () => {
    if (!validNumber) {
      setStatusKind("error");
      setStatus("Invalid phone number");
      return;
    }

    if (!account) {
      setStatusKind("error");
      setStatus("Microsoft login is not ready");
      return;
    }

    setWorking(true);
    setStatusKind("info");
    setStatus("Sending reminder...");

    try {
      const token = await acquireToken(instance, account);
      const data = await sendReminder(token.accessToken, {
        destination: normalizedNumber,
        parentName: parent?.fullname || null,
        studentId: student?.contactid || null,
        studentName: student?.fullname || null,
        mode: "playback",
      });

      const nextStatus = getReminderStatus(data);
      setStatusKind(nextStatus.kind);
      setStatus(nextStatus.message);
    } catch (reminderError) {
      setStatusKind("error");
      setStatus(getApiErrorMessage(reminderError, "Unable to send reminder"));
    } finally {
      setWorking(false);
    }
  }, [account, instance, normalizedNumber, parent, student, validNumber]);

  return {
    working,
    status,
    statusKind,
    normalizedNumber,
    validNumber,
    disabled,
    send,
  };
};
